import { Component } from '@theme/component';
import { fetchConfig, prefersReducedMotion } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';
import { getVisibleCartIcon } from '@theme/fly-to-cart';

/**
 * SOLEYA — Cart Upsell Item
 *
 * A self-contained, instantly-purchasable recommendation card rendered
 * inside the cart drawer's "Complete Your Order" module (see
 * snippets/soleya-cart-upsell.liquid). Mirrors the AJAX add-to-cart +
 * confirmation pattern already built for the product card Quick Add
 * (assets/quick-add.js), adapted for the cart drawer context:
 *
 * - Single-variant products: clicking "Add" adds the item directly.
 * - Multi-variant products: clicking "Add" opens a small inline option
 *   selector (size, colour, or whichever option varies) inside the card;
 *   selecting a value adds that variant.
 *
 * Adding an item here posts straight to the Cart AJAX API and dispatches a
 * CartAddEvent with the response's `sections` payload, exactly like the
 * theme's native add-to-cart flow. The existing cart-items-component
 * listener (assets/component-cart-items.js) takes care of morphing the
 * entire drawer — items, subtotal, free shipping hero, and this upsell
 * module itself — with no page refresh.
 *
 * The confirmation toast is appended to `document.body` rather than kept
 * inside this element, since this card's own DOM node is likely to be
 * replaced/removed a moment later when the drawer section refreshes (the
 * just-added product drops out of the recommendation list). Keeping the
 * toast outside the morphed subtree means it always finishes its own
 * lifecycle regardless of when that refresh lands.
 *
 * NOTE: this module is loaded from snippets/cart-items-component.liquid,
 * OUTSIDE the <cart-items-component> subtree, because that subtree is
 * wholesale replaced on every cart update and imported <script> tags never
 * execute. See the comment in that snippet for the full rationale.
 */
const CHECK_SVG =
  '<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 7.2L5.5 10.2L11.5 3.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

export class SoleyaCartUpsellItem extends Component {
  get #optionPopover() {
    return this.querySelector('[data-upsell-option-popover]');
  }

  get #addButton() {
    return this.querySelector('[data-upsell-add-button]');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('pointerdown', this.#handleOutsideInteraction, { capture: true });
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * Handles a click on the card's Add button. Adds directly if the product
   * has a single variant, otherwise opens the inline option selector.
   * @param {MouseEvent} event
   */
  handleAddClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.hasAttribute('data-loading')) return;

    const singleVariantId = this.dataset.singleVariantId;
    if (singleVariantId) {
      this.#addToCart(singleVariantId, this.#addButton);
      return;
    }

    if (this.hasAttribute('data-option-popover-open')) {
      this.#closeOptionPopover();
    } else {
      this.#openOptionPopover();
    }
  };

  /**
   * Handles a click on an option chip inside the inline popover.
   * @param {MouseEvent} event
   */
  handleOptionSelect = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target;
    const button = target instanceof HTMLElement ? target.closest('[data-variant-id]') : null;
    if (!button || button.hasAttribute('disabled')) return;

    const variantId = /** @type {HTMLElement} */ (button).dataset.variantId;
    if (!variantId) return;

    this.#closeOptionPopover();
    this.#addToCart(variantId, /** @type {HTMLElement} */ (button));
  };

  #openOptionPopover = () => {
    const popover = this.#optionPopover;
    const addButton = this.#addButton;
    if (!popover) return;

    document.querySelectorAll('soleya-cart-upsell-item[data-option-popover-open]').forEach((openItem) => {
      if (openItem instanceof SoleyaCartUpsellItem && openItem !== this) {
        openItem.#closeOptionPopover();
      }
    });

    popover.removeAttribute('inert');
    popover.setAttribute('aria-hidden', 'false');
    this.setAttribute('data-option-popover-open', 'true');
    addButton?.setAttribute('aria-expanded', 'true');

    document.addEventListener('pointerdown', this.#handleOutsideInteraction, { capture: true });
    document.addEventListener('keydown', this.#handleKeydown);
  };

  #closeOptionPopover = () => {
    const popover = this.#optionPopover;
    const addButton = this.#addButton;
    if (!popover) return;

    popover.setAttribute('inert', '');
    popover.setAttribute('aria-hidden', 'true');
    this.removeAttribute('data-option-popover-open');
    addButton?.setAttribute('aria-expanded', 'false');

    document.removeEventListener('pointerdown', this.#handleOutsideInteraction, { capture: true });
    document.removeEventListener('keydown', this.#handleKeydown);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleOutsideInteraction = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) {
      this.#closeOptionPopover();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#closeOptionPopover();
      this.#addButton?.focus();
    }
  };

  /**
   * Adds a variant to the cart via the Cart AJAX API, then dispatches a
   * CartAddEvent so the rest of the theme (cart drawer contents, badge,
   * free shipping hero) updates instantly, exactly like the native
   * add-to-cart flow.
   *
   * SOLEYA FIX: `data-loading` is now cleared on the success path too, not
   * only on failure. It was previously left set on the assumption that this
   * card would be removed a moment later by the drawer refresh; when that
   * refresh didn't replace the card, the CSS rule
   * `soleya-cart-upsell-item[data-loading] .soleya-cart-upsell__add`
   * (opacity .45 + pointer-events: none) plus the guard at the top of
   * handleAddClick left the button permanently dead for the rest of the
   * page's life. Clearing it keeps the double-click protection during the
   * request while guaranteeing the button always recovers afterwards.
   *
   * @param {string} variantId
   * @param {HTMLElement | null} sourceElement
   */
  async #addToCart(variantId, sourceElement) {
    this.setAttribute('data-loading', 'true');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [...cartItemsComponents]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
      .filter(Boolean);

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: sectionIds.join(','),
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const result = await response.json();

      if (result.status) {
        console.error('[soleya-cart-upsell] Failed to add item to cart.', result.message);
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(result, this.dataset.productId || 'soleya-cart-upsell-item', {
          source: 'soleya-cart-upsell',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: result.sections,
        })
      );

      this.#playConfirmation(sourceElement);
    } catch (error) {
      console.error('[soleya-cart-upsell] Failed to add item to cart.', error);
    } finally {
      this.removeAttribute('data-loading');
    }
  }

  /**
   * Plays a tasteful confirmation: an optional fly-to-cart animation from
   * the card toward the currently visible cart icon, plus a brief
   * "Added to your bag" toast appended to document.body (independent of
   * this card's DOM, which may be replaced by the imminent drawer refresh).
   * @param {HTMLElement | null} sourceElement
   */
  #playConfirmation(sourceElement) {
    if (!prefersReducedMotion() && sourceElement) {
      const cartIcon = getVisibleCartIcon();
      const image = this.dataset.productImage;

      if (cartIcon) {
        const flyToCart = document.createElement('fly-to-cart');
        flyToCart.classList.add('fly-to-cart--quick');
        if (image) flyToCart.style.setProperty('background-image', `url(${image})`);
        flyToCart.style.setProperty('--start-opacity', '0');
        flyToCart.source = sourceElement;
        flyToCart.destination = cartIcon;
        document.body.appendChild(flyToCart);
      }
    }

    const toast = document.createElement('div');
    toast.className = 'soleya-upsell-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML = `<span class="soleya-upsell-toast__icon" aria-hidden="true">${CHECK_SVG}</span><span>Added to your bag</span>`;
    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.classList.add('soleya-upsell-toast--visible');
      });
    });

    setTimeout(() => {
      toast.classList.remove('soleya-upsell-toast--visible');
      toast.addEventListener(
        'transitionend',
        () => {
          toast.remove();
        },
        { once: true }
      );
      // Fallback removal in case transitionend doesn't fire (e.g. reduced motion).
      setTimeout(() => toast.remove(), 500);
    }, 1500);
  }
}

if (!customElements.get('soleya-cart-upsell-item')) {
  customElements.define('soleya-cart-upsell-item', SoleyaCartUpsellItem);
}
